import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  TextField,
  Box,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActionArea,
  Chip
} from '@mui/material';
import { Account } from '../../state/types';
import { useAccountContext } from '../../context/AccountContext';

type SortKey = keyof Account | ''; // Safe list of sortable keys

const AccountCard: React.FC<{ user: Account; onClick: () => void }> = ({ user, onClick }) => {
  return (
    <Card sx={{ mb: 1.5 }} data-cy={`accounts-card-${user.username}`}>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="div" noWrap>
              {user.first_name} {user.last_name}
            </Typography>
            <Chip label={user.role} size="small" color={user.role === 'Admin' ? 'primary' : 'default'} />
          </Box>
          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
          <Typography variant="body2" color="text.secondary">{user.phone_number}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Status: <strong>{user.status}</strong>
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const AccountsTable: React.FC = () => {
  const { openEditAccountModal, openAddAccountModal, accounts: accountList, fetchAccounts } = useAccountContext();
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('email');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedAccounts = useMemo(() => {
    const filtered = accountList.filter(user => {
      const query = searchTerm.toLowerCase();
      return (
        user.email.toLowerCase().includes(query) ||
        user.first_name.toLowerCase().includes(query) ||
        user.last_name.toLowerCase().includes(query) ||
        user.phone_number?.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        user.status?.toLowerCase().includes(query)
      );
    });

    if (!sortBy) return filtered;

    return filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return 0;
    });
  }, [accountList, searchTerm, sortBy, sortDirection]);

  const headers: { label: string; key: SortKey }[] = [
    { label: 'Email', key: 'email' },
    { label: 'First Name', key: 'first_name' },
    { label: 'Last Name', key: 'last_name' },
    { label: 'Phone Number', key: 'phone_number' },
    { label: 'Role', key: 'role' },
    { label: 'Status', key: 'status' },
    { label: 'Last Login', key: 'lastLoginDate' },
  ];

  return (
    <Box sx={{ 
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0
    }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={isMobile ? 12 : 8} md={6}>
            <TextField label="Search" fullWidth size="small" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-cy="accounts-search-input" />
          </Grid>
          <Grid item xs={12} sm={isMobile ? 12 : 4} md={2}>
            <Button variant="contained" fullWidth onClick={openAddAccountModal} data-cy="accounts-add-btn">
              Add Account
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isMobile ? (
        // --- Mobile Card List ---
        <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          }
        }}
        >
          {filteredAndSortedAccounts.map(user => (
            <AccountCard key={user.username} user={user} onClick={() => openEditAccountModal(user)} />
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '48vh',
            overflow: 'auto',
            maxWidth: '100%',
          }}
        >
          <Table sx={{ minWidth: 900 }}>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}
            >
              <TableRow>
                {headers.map(({ label, key }) => (
                  <TableCell key={key}>
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortDirection : 'asc'}
                      onClick={() => handleSort(key)}
                      data-cy={`accounts-sort-${key}`}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredAndSortedAccounts.map(user => (
                <TableRow
                  key={user.username}
                  hover
                  onClick={() => openEditAccountModal(user)}
                  sx={{ cursor: 'pointer' }}
                  data-cy={`accounts-table-row-${user.username}`}
                >
                  <TableCell><Typography>{user.email}</Typography></TableCell>
                  <TableCell><Typography>{user.first_name}</Typography></TableCell>
                  <TableCell><Typography>{user.last_name}</Typography></TableCell>
                  <TableCell><Typography>{user.phone_number}</Typography></TableCell>
                  <TableCell><Typography>{user.role}</Typography></TableCell>
                  <TableCell><Typography>{user.status}</Typography></TableCell>
                  <TableCell><Typography>{user.lastLoginDate}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AccountsTable;
